import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement, BarElement, ArcElement,
  CategoryScale, LinearScale, PointElement,
  Tooltip, Legend, Filler,
} from "chart.js";
import {
  Users, ShoppingBag, TrendingUp, Truck,
  Store, RefreshCw, Activity,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getPublicStats,
  getPublicUsers,
  getPublicOrders,
  getPublicAnalytics,
} from "../api/apiServices";

ChartJS.register(
  LineElement, BarElement, ArcElement,
  CategoryScale, LinearScale, PointElement,
  Tooltip, Legend, Filler,
);

/* ─────────────────────── constants ─────────────────────── */
const STATUS_COLORS = {
  pending:     { bg: "bg-amber-100",  text: "text-amber-700",  hex: "#B45309" },
  processing:  { bg: "bg-blue-100",   text: "text-blue-700",   hex: "#1D4ED8" },
  ready:       { bg: "bg-purple-100", text: "text-purple-700", hex: "#7C3AED" },
  shipped:     { bg: "bg-indigo-100", text: "text-indigo-700", hex: "#4338CA" },
  delivered:   { bg: "bg-green-100",  text: "text-green-700",  hex: "#15803D" },
  "picked-up": { bg: "bg-teal-100",   text: "text-teal-700",   hex: "#0F766E" },
  cancelled:   { bg: "bg-red-100",    text: "text-red-700",    hex: "#B91C1C" },
  confirmed:   { bg: "bg-emerald-100",text: "text-emerald-700",hex: "#065F46" },
};

const CHART_COLORS = [
  "#1D4ED8","#7C3AED","#15803D","#B45309",
  "#B91C1C","#0F766E","#4338CA","#92400E",
];

const fmtMoney = (n) => {
  if (!n) return "₦0";
  if (n >= 1_000_000) return `₦${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `₦${(n / 1_000).toFixed(0)}k`;
  return `₦${n}`;
};

const initials = (f, l) =>
  `${(f || "?")[0]}${(l || "")[0]}`.toUpperCase();

/* ─────────────────────── skeleton ─────────────────────── */
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />
);

/* ─────────────────────── stat card ─────────────────────── */
function StatCard({ icon: Icon, label, value, color, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-800">{(value || 0).toLocaleString()}</p>
    </motion.div>
  );
}

/* ─────────────────────── status badge ─────────────────────── */
function StatusBadge({ status }) {
  const m = STATUS_COLORS[status] || { bg: "bg-gray-100", text: "text-gray-500" };
  return (
    <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>
      {status}
    </span>
  );
}

/* ─────────────────────── pagination ─────────────────────── */
function Pagination({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-50">
      <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
      <div className="flex gap-1">
        <button
          disabled={page <= 1}
          onClick={onPrev}
          className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-all"
        >
          <ChevronLeft size={13} />
        </button>
        <button
          disabled={page >= totalPages}
          onClick={onNext}
          className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-all"
        >
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────── main page ─────────────────────── */
export default function PublicDashboard() {
  const [stats,     setStats]     = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users,     setUsers]     = useState([]);
  const [orders,    setOrders]    = useState([]);
  const [activity,  setActivity]  = useState([
    { msg: "Platform dashboard loaded", type: "system", time: Date.now() },
  ]);

  const [userPage,   setUserPage]   = useState(1);
  const [orderPage,  setOrderPage]  = useState(1);
  const [userTotal,  setUserTotal]  = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);

  const [loadingStats,     setLoadingStats]     = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingUsers,     setLoadingUsers]     = useState(true);
  const [loadingOrders,    setLoadingOrders]    = useState(true);

  const addActivity = useCallback((msg, type = "system") => {
    setActivity((prev) =>
      [{ msg, type, time: Date.now() }, ...prev].slice(0, 20)
    );
  }, []);

  /* ── fetchers ── */
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const data = await getPublicStats();
      setStats(data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const data = await getPublicAnalytics();
      setAnalytics(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoadingUsers(true);
    try {
      const data = await getPublicUsers(page, 15);
      setUsers(data.users);
      setUserTotal(data.total);
      setUserPage(page);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchOrders = useCallback(async (page = 1) => {
    setLoadingOrders(true);
    try {
      const data = await getPublicOrders(page, 15);
      setOrders(data.orders);
      setOrderTotal(data.total);
      setOrderPage(page);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchStats();
    fetchAnalytics();
    fetchUsers(1);
    fetchOrders(1);
    addActivity("Dashboard refreshed");
  }, [fetchStats, fetchAnalytics, fetchUsers, fetchOrders, addActivity]);

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
    fetchUsers(1);
    fetchOrders(1);
  }, []);

  /* ── chart options ── */
  const chartBase = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: { color: "#9CA3AF", font: { size: 10 } },
        grid:  { color: "rgba(0,0,0,0.04)" },
      },
      y: {
        ticks: { color: "#9CA3AF", font: { size: 10 } },
        grid:  { color: "rgba(0,0,0,0.04)" },
        beginAtZero: true,
      },
    },
  };

  const lineData = analytics ? {
    labels: analytics.daily.labels,
    datasets: [{
      label: "Orders",
      data:  analytics.daily.orders,
      borderColor: "#1D4ED8",
      backgroundColor: "rgba(29,78,216,0.07)",
      tension: 0.4, fill: true, pointRadius: 3, borderWidth: 2,
    }],
  } : null;

  const barData = analytics ? {
    labels: analytics.daily.labels,
    datasets: [{
      label: "Revenue",
      data:  analytics.daily.revenue,
      backgroundColor: "rgba(124,58,237,0.55)",
      borderColor: "#7C3AED",
      borderWidth: 1,
      borderRadius: 4,
    }],
  } : null;

  const donutData = analytics ? {
    labels: analytics.statusBreakdown.map((s) => s.status),
    datasets: [{
      data: analytics.statusBreakdown.map((s) => s.count),
      backgroundColor: analytics.statusBreakdown.map(
        (s) => STATUS_COLORS[s.status]?.hex || "#9CA3AF"
      ),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  } : null;

  const USER_PAGES  = Math.ceil(userTotal  / 15);
  const ORDER_PAGES = Math.ceil(orderTotal / 15);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── header ── */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-10">
        <div className="max-w-7xl mx-auto flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1">Platform Dashboard</h1>
            <p className="text-indigo-200 text-sm">
              Live overview of orders, users, and platform activity
            </p>
          </div>
          <button
            onClick={refreshAll}
            className="flex items-center gap-2 text-sm font-semibold bg-white/15 hover:bg-white/25 border border-white/30 px-4 py-2 rounded-xl transition-all"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {/* ── stat cards ── */}
        {loadingStats ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={Users}       label="Total Users"   value={stats.users.total}   color="bg-blue-500"   delay={0} />
            <StatCard icon={ShoppingBag} label="Total Orders"  value={stats.orders.total}  color="bg-purple-500" delay={0.05} />
            <StatCard icon={TrendingUp}  label="Gross Revenue" value={stats.gmv}            color="bg-green-500"  delay={0.1} />
            <StatCard icon={Store}       label="Vendors"        value={stats.vendors.total} color="bg-orange-500" delay={0.15} />
            <StatCard icon={Truck}       label="Riders"         value={stats.riders.total}  color="bg-red-500"    delay={0.2} />
          </div>
        )}

        {/* ── GMV highlight ── */}
        {stats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-6 flex items-center justify-between"
          >
            <div>
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-1">
                Total Gross Merchandise Value
              </p>
              <p className="text-4xl font-bold text-indigo-700">{fmtMoney(stats.gmv)}</p>
              <p className="text-xs text-indigo-400 mt-1">
                Cumulative order value across all stores
              </p>
            </div>
            <TrendingUp size={52} className="text-indigo-200" />
          </motion.div>
        )}

        {/* ── charts ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* orders line chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-4">Orders — last 14 days</h3>
            {loadingAnalytics || !lineData
              ? <Skeleton className="h-48" />
              : <div style={{ height: 200 }}><Line data={lineData} options={chartBase} /></div>
            }
          </div>

          {/* status donut */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-4">Order status breakdown</h3>
            {loadingAnalytics || !donutData
              ? <Skeleton className="h-48" />
              : (
                <>
                  <div style={{ height: 160 }}>
                    <Doughnut
                      data={donutData}
                      options={{ ...chartBase, scales: undefined, cutout: "65%" }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-3">
                    {analytics.statusBreakdown.map((s) => (
                      <span key={s.status} className="flex items-center gap-1 text-[10px] text-gray-500">
                        <span
                          className="w-2 h-2 rounded-sm inline-block"
                          style={{ background: STATUS_COLORS[s.status]?.hex || "#ccc" }}
                        />
                        {s.status} · {s.count}
                      </span>
                    ))}
                  </div>
                </>
              )
            }
          </div>
        </div>

        {/* revenue bar chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Daily revenue (₦)</h3>
          {loadingAnalytics || !barData
            ? <Skeleton className="h-44" />
            : (
              <div style={{ height: 190 }}>
                <Bar
                  data={barData}
                  options={{
                    ...chartBase,
                    plugins: {
                      ...chartBase.plugins,
                      tooltip: { callbacks: { label: (ctx) => fmtMoney(ctx.raw) } },
                    },
                  }}
                />
              </div>
            )
          }
        </div>

        {/* top stores */}
        {analytics?.topStores?.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-5">Top stores by order volume</h3>
            <div className="space-y-3">
              {analytics.topStores.map((store, i) => {
                const max = analytics.topStores[0].orders;
                return (
                  <div key={store.name} className="flex items-center gap-3">
                    <span className="text-xs text-gray-300 w-4 font-mono">{i + 1}</span>
                    <span className="text-sm text-gray-700 w-40 truncate font-medium">{store.name}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.round((store.orders / max) * 100)}%`,
                          background: CHART_COLORS[i % CHART_COLORS.length],
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-8 text-right font-semibold">{store.orders}</span>
                    <span className="text-xs text-gray-400 w-16 text-right">{fmtMoney(store.revenue)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── users + orders ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* users list */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
              <div className="flex items-center gap-2">
                <Users size={15} className="text-gray-400" />
                <span className="text-sm font-semibold text-gray-600">Users</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">
                  {userTotal.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="divide-y divide-gray-50">
              {loadingUsers
                ? Array(5).fill(0).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-6 py-3">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-2.5 w-48" />
                      </div>
                    </div>
                  ))
                : users.map((u) => (
                  <div key={u.userId || u._id} className="flex items-center gap-3 px-6 py-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {initials(u.firstName || u.userName, u.lastName || u.email)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 truncate">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-gray-400 truncate capitalize">
                        {u.role} · {u.location?.city || u.email?.split("@")[0]}
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold text-gray-400 capitalize flex-shrink-0">
                      {u.role}
                    </span>
                  </div>
                ))
              }
            </div>

            <Pagination
              page={userPage}
              totalPages={USER_PAGES}
              onPrev={() => fetchUsers(userPage - 1)}
              onNext={() => fetchUsers(userPage + 1)}
            />
          </div>

          {/* orders list */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
              <div className="flex items-center gap-2">
                <ShoppingBag size={15} className="text-gray-400" />
                <span className="text-sm font-semibold text-gray-600">Orders</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">
                  {orderTotal.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="divide-y divide-gray-50">
              {loadingOrders
                ? Array(5).fill(0).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-6 py-3">
                      <Skeleton className="w-8 h-8 rounded-lg" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3 w-28" />
                        <Skeleton className="h-2.5 w-40" />
                      </div>
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                  ))
                : orders.map((o) => (
                  <div key={o._id} className="flex items-center gap-3 px-6 py-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag size={14} className="text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 truncate">{o.orderId}</p>
                      <p className="text-xs text-gray-400 truncate">{o.storeName}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-gray-800">
                        ₦{o.totalAmount?.toLocaleString()}
                      </p>
                      <StatusBadge status={o.status} />
                    </div>
                  </div>
                ))
              }
            </div>

            <Pagination
              page={orderPage}
              totalPages={ORDER_PAGES}
              onPrev={() => fetchOrders(orderPage - 1)}
              onNext={() => fetchOrders(orderPage + 1)}
            />
          </div>
        </div>

        {/* ── activity feed ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={15} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-600">Activity</h3>
          </div>
          <div className="space-y-0">
            {activity.map((a, i) => {
              const elapsed = Math.round((Date.now() - a.time) / 1000);
              const timeStr =
                elapsed < 60   ? `${elapsed}s ago` :
                elapsed < 3600 ? `${Math.floor(elapsed / 60)}m ago` :
                                 `${Math.floor(elapsed / 3600)}h ago`;
              return (
                <div key={i} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600">{a.msg}</p>
                    <p className="text-xs text-gray-300">{timeStr}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
